import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Button, Card, Section, Stat } from '@/components/ui';
import { Progression, StackContract } from '@/components/charts';
import { SITE } from '@/lib/site';

const steps = [
  { n: '01', title: 'Assessment', detail: 'Your own workload, replayed on the Fnk stack against the one you run today. Fixed scope, and the acceptance thresholds Fnk is then held to.', meta: '2–3 weeks' },
  { n: '02', title: 'Licence, or a box', detail: 'The stack on your GPUs, priced per GPU per year — or the complete system with the Harness, connectors and console preinstalled.', meta: 'per GPU · partial box · full box' },
  { n: '03', title: 'Production', detail: 'Connectors mapped to your systems, sources ingested, acceptance tested against the written thresholds, then live with support.', meta: '9–16 weeks to live' },
];

export function LandingPage() {
  return (
    <div>
      {/* Hero */}
      <header className="ruled border-b border-line">
        <div className="mx-auto max-w-site px-4 py-16 sm:px-6 md:py-28">
          <p className="label mb-6">FNK Labs · Enterprise AI infrastructure</p>
          <h1 className="max-w-4xl text-hero-mobile font-semibold tracking-heading md:text-hero-desktop">
            Enterprise AI, <span className="text-accent-ink">built as one system.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-2 sm:text-lg">
            An agent framework, a control layer and an inference engine that share one capacity contract — plus the Harness that turns them into a
            governed service. Licensed per GPU on your hardware, or delivered as a box where nothing leaves your environment.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href={SITE.assessmentMailto} className="w-full sm:w-auto">
              <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>
                Request an assessment
              </Button>
            </a>
            <Link to="/benchmark" className="w-full sm:w-auto">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="h-4 w-4" />}>
                See the benchmark
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* The measured result, in three numbers */}
      <section className="border-b border-line bg-paper-2">
        <div className="mx-auto grid max-w-site grid-cols-1 gap-6 px-4 py-10 sm:grid-cols-3 sm:px-6">
          <Stat value="2.0×" label="Completed requests per second over Spring MVC + LangChain4j + vLLM, same GPU" tone="accent" />
          <Stat value="1.6×" label="Lower mean latency at concurrency 64" />
          <Stat value="1.4× → 2.0×" label="Lead at every point of the concurrency sweep" />
        </div>
      </section>

      {/* The shift */}
      <Section n="01" kicker="The shift" title="Rented, then assembled, then one system." lead="Enterprise inference is leaving the public cloud. What replaces it today is a framework bolted onto an engine over HTTP — and that boundary is where the accelerators go to waste.">
        <Progression />
      </Section>

      {/* Two halves, one contract */}
      <Section n="02" kicker="The technical win" title="Capacity flows up. Cancellation flows down." lead="The assembled stack cannot see runtime capacity from the framework, or the business transaction from the engine. So it overprovisions, collapses under load and pays for work nobody is waiting for. Fnk replaces the boundary with a contract." tone="white">
        <Card variant="raised" className="p-5 sm:p-8">
          <StackContract />
        </Card>
      </Section>

      {/* How it works */}
      <Section n="03" kicker="How it works" title="Three steps. The first one is small.">
        <ol className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-3">
          {steps.map((s) => (
            <li key={s.n}>
              <Card variant="raised" className="flex h-full flex-col p-6">
                <span className="font-mono text-xs text-accent-ink">{s.n}</span>
                <h3 className="mt-2 text-lg font-semibold tracking-tight">{s.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-2">{s.detail}</p>
                <p className="mt-4 border-t border-line pt-3 font-mono text-[11px] tabular text-ink-3">{s.meta}</p>
              </Card>
            </li>
          ))}
        </ol>
      </Section>

      {/* Built on Fnk */}
      <Section n="04" kicker="First customer" title="A production workload runs on it today." tone="ink" lead="DialogMe, a conversational AI platform, is Fnk’s first customer and runs its production workload on the Fnk stack.">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <p className="max-w-2xl text-sm leading-relaxed text-ink-2">
            Real conversations from real businesses harden the framework and the engine before either reaches an enterprise acceptance test.
          </p>
          <a href={SITE.dialogme} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
            <Button variant="secondary" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>
              dialogme.ai
            </Button>
          </a>
        </div>
      </Section>

      {/* CTA */}
      <section className="border-t border-line">
        <div className="mx-auto max-w-site px-4 py-14 sm:px-6 md:py-20">
          <Card variant="accent" className="p-6 sm:p-8 md:p-10">
            <h2 className="text-section-mobile font-semibold tracking-heading md:text-section-desktop">Replay your workload on it.</h2>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink-2">
              An assessment measures the multiple on your own traffic and hardware class, and how many GPUs the stack would save you.
              If the number is not there, you know in three weeks.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a href={SITE.assessmentMailto} className="w-full sm:w-auto">
                <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>
                  Request an assessment
                </Button>
              </a>
              <Link to="/pricing" className="w-full sm:w-auto">
                <Button variant="ghost" size="lg" className="w-full sm:w-auto">
                  How the licence is priced
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
